import { useQuery } from '@tanstack/react-query';

import useApi from '@/hooks/useApi/useApi';
import { queryKeys } from '@/lib/queryKeys';
import { Project, ProjectTimesheetReport, Task, TaskTimesheetReport } from '@/types/types';

// 1. Project Report
export const useProjectTimesheetReport = (
  projectId?: string | number,
  startDate?: string,
  endDate?: string
) => {
  const { axiosInstance: api } = useApi();

  return useQuery<ProjectTimesheetReport>({
    queryKey: queryKeys.timesheetReports.project(projectId, startDate, endDate).queryKey,
    queryFn: async () => {
      if (!projectId) return null;
      let url = `/api/v1/timesheet_reports/project/${projectId}`;
      const params = [];
      if (startDate) params.push(`start_date=${startDate}`);
      if (endDate) params.push(`end_date=${endDate}`);
      if (params.length) url += `?${params.join('&')}`;
      const { data } = await api.get(url);
      return data;
    },
    enabled: !!projectId,
  });
};

// 2. Account Utilization
export const useAccountUtilizationReport = (
  accountId?: string | number,
  startDate?: string,
  endDate?: string
) => {
  const { axiosInstance: api } = useApi();

  return useQuery({
    queryKey: queryKeys.timesheetReports.accountUtilization(accountId, startDate, endDate).queryKey,
    queryFn: async () => {
      if (!accountId || !startDate || !endDate) return null;
      const url = `/api/v1/timesheet_reports/account/${accountId}/utilization?start_date=${startDate}&end_date=${endDate}`;
      const { data } = await api.get(url);
      return data;
    },
    enabled: !!accountId && !!startDate && !!endDate,
  });
};

export type CompanyTimesheetOverview = {
  company: {
    id: number;
    name: string;
  };
  period: {
    start_date: string;
    end_date: string;
  };
  summary: {
    total_hours: string;
    billable_hours: string;
    total_cost: string;
    entries_count: number;
    active_accounts: number;
  };
  by_status: {
    draft: number;
    submitted: number;
  };
  by_project: Record<string, string>;
  top_contributors: Array<{
    registerable_type: 'Account' | 'Partner';
    registerable_id: number;
    name: string;
    hours: number;
  }>;
};

// 3. Company Overview
export const useCompanyTimesheetOverview = (
  startDate?: string,
  endDate?: string,
  registerableId?: string | number,
  registerableType?: 'Account' | 'Partner',
  projectId?: Project['id'],
  taskId?: Task['id']
) => {
  const { axiosInstance: api } = useApi();

  return useQuery<CompanyTimesheetOverview>({
    queryKey: queryKeys.timesheetReports.companyOverview(
      startDate,
      endDate,
      registerableId,
      registerableType,
      projectId,
      taskId
    ).queryKey,
    queryFn: async () => {
      if (!startDate || !endDate) return null;
      const url = `/api/v1/timesheet_reports/company_overview`;
      const { data } = await api.get(url, {
        params: {
          start_date: startDate,
          end_date: endDate,
          registerable_id: registerableId || '',
          registerable_type: registerableType || '',
          project_id: projectId || '',
          task_id: taskId || '',
        },
      });
      return data;
    },
    enabled: !!startDate && !!endDate,
  });
};

export const useTaskTimesheetReport = (taskId: Task['id']) => {
  const { axiosInstance: api } = useApi();

  return useQuery({
    queryKey: queryKeys.timesheetReports.task(taskId).queryKey,
    queryFn: async () => {
      const { data } = await api.get<TaskTimesheetReport>(
        '/api/v1/timesheet_reports/task/' + taskId
      );
      return data;
    },
    enabled: !!taskId,
  });
};
